/**
 * v2 Settings → Alarm rules editor.
 *
 * CRUD over ``alarm_severity_rules`` (SP3 backend). Each rule is a
 * pattern (literal substring OR regex) that classifies an incoming
 * alarm_name into a severity. Worker reads enabled rules at the top
 * of every parse_alarms run; first match wins, ``info`` default.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Save, Trash2, X } from "lucide-react";
import { useState } from "react";

import { api } from "../../../api/client";
import { Badge } from "../../components/Badge";
import { SeverityPill } from "../../components/SeverityPill";
import { useToast } from "../../components/Toast";
import { HelpButton } from "../../help/HelpPanel";

type Rule = {
  id: number;
  pattern: string;
  pattern_type: "literal" | "regex";
  severity: string;
  enabled: boolean;
  created_by: string;
  created_at: string;
};

type RuleListResponse = { rules: Rule[] };

const SEVERITY_OPTIONS = ["critical", "major", "minor", "warning", "info"];

export function AlarmRulesEditor() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery<RuleListResponse>({
    queryKey: ["v2", "alarm-rules"],
    queryFn: async () => {
      const res = await api.get<RuleListResponse>("/alarm-rules");
      return res.data;
    },
  });
  const [editing, setEditing] = useState<number | "new" | null>(null);

  const create = useMutation({
    mutationFn: async (body: Omit<Rule, "id" | "created_by" | "created_at">) =>
      api.post("/alarm-rules", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["v2", "alarm-rules"] });
      setEditing(null);
      toast.push("Rule added", "success");
    },
    onError: () => toast.push("Failed to add rule", "error"),
  });

  const update = useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: number;
      pattern: string;
      pattern_type: "literal" | "regex";
      severity: string;
      enabled: boolean;
    }) => api.put(`/alarm-rules/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["v2", "alarm-rules"] });
      setEditing(null);
      toast.push("Rule updated", "success");
    },
    onError: () => toast.push("Failed to update rule", "error"),
  });

  const del = useMutation({
    mutationFn: async (id: number) => api.delete(`/alarm-rules/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["v2", "alarm-rules"] });
      toast.push("Rule deleted", "success");
    },
    onError: () => toast.push("Delete failed", "error"),
  });

  const rules = q.data?.rules ?? [];

  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-baseline justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-slate-100">Alarm rules</h1>
            <HelpButton topic="settings-alarm-rules" />
          </div>
          <p className="text-xs text-slate-500">
            Pattern → severity mappings the worker applies when persisting
            alarms from a parse_alarms run. First enabled rule wins;
            unmatched names default to <em>info</em>.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEditing("new")}
          className="inline-flex items-center gap-1 rounded border border-emerald-700 bg-emerald-900/40 px-2 py-1 text-xs text-emerald-200 hover:bg-emerald-900/60"
        >
          <Plus className="h-3 w-3" />
          Add rule
        </button>
      </header>

      <div className="rounded-lg bg-slate-900 shadow-border p-4">
        {editing === "new" && (
          <RuleForm
            onSave={(body) => create.mutate(body)}
            onCancel={() => setEditing(null)}
            saving={create.isPending}
          />
        )}
        {q.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : rules.length === 0 ? (
          <p className="text-xs text-slate-500">
            No rules yet. Add one above to start classifying alarms.
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1">Pattern</th>
                <th className="px-2 py-1">Type</th>
                <th className="px-2 py-1">Severity</th>
                <th className="px-2 py-1">Enabled</th>
                <th className="px-2 py-1">By</th>
                <th className="px-2 py-1 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/40 text-slate-300">
              {rules.map((r) =>
                editing === r.id ? (
                  <tr key={r.id} className="bg-slate-950/40">
                    <td colSpan={6} className="px-2 py-2">
                      <RuleForm
                        initial={r}
                        onSave={(body) => update.mutate({ id: r.id, ...body })}
                        onCancel={() => setEditing(null)}
                        saving={update.isPending}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr key={r.id} className={r.enabled ? "" : "opacity-50"}>
                    <td className="px-2 py-1.5 font-mono">{r.pattern}</td>
                    <td className="px-2 py-1.5">
                      <Badge tone={r.pattern_type === "regex" ? "info" : "neutral"}>
                        {r.pattern_type}
                      </Badge>
                    </td>
                    <td className="px-2 py-1.5">
                      <SeverityPill severity={r.severity} />
                    </td>
                    <td className="px-2 py-1.5">
                      {r.enabled ? (
                        <Badge tone="success">enabled</Badge>
                      ) : (
                        <Badge tone="muted">disabled</Badge>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-slate-400">
                      {r.created_by}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setEditing(r.id)}
                          className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300"
                        >
                          <Pencil className="h-3 w-3" />
                          edit
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            window.confirm(
                              `Delete rule "${r.pattern}"? Future parse_alarms runs will fall back to other rules.`,
                            ) && del.mutate(r.id)
                          }
                          className="inline-flex items-center gap-1 text-red-300 hover:text-red-200"
                        >
                          <Trash2 className="h-3 w-3" />
                          del
                        </button>
                      </div>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function RuleForm({
  initial,
  onSave,
  onCancel,
  saving,
}: {
  initial?: Rule;
  onSave: (body: {
    pattern: string;
    pattern_type: "literal" | "regex";
    severity: string;
    enabled: boolean;
  }) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [pattern, setPattern] = useState(initial?.pattern ?? "");
  const [patternType, setPatternType] = useState<"literal" | "regex">(
    initial?.pattern_type ?? "literal",
  );
  const [severity, setSeverity] = useState(initial?.severity ?? "major");
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!pattern.trim()) return;
        onSave({
          pattern: pattern.trim(),
          pattern_type: patternType,
          severity,
          enabled,
        });
      }}
      className="grid gap-2 sm:grid-cols-[3fr,1fr,1fr,auto,auto]"
    >
      <input
        type="text"
        value={pattern}
        onChange={(e) => setPattern(e.target.value)}
        placeholder={
          patternType === "regex" ? "^Port \\d+/\\d+ LOS$" : "LOS"
        }
        className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm font-mono text-slate-100 focus-visible:border-emerald-700 focus-visible:outline-none"
        autoFocus
      />
      <select
        value={patternType}
        onChange={(e) => setPatternType(e.target.value as "literal" | "regex")}
        className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100"
      >
        <option value="literal">literal</option>
        <option value="regex">regex</option>
      </select>
      <select
        value={severity}
        onChange={(e) => setSeverity(e.target.value)}
        className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100"
      >
        {SEVERITY_OPTIONS.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      <label className="inline-flex items-center gap-1 text-xs text-slate-300">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        enabled
      </label>
      <div className="flex items-center gap-1">
        <button
          type="submit"
          disabled={saving || !pattern.trim()}
          className="inline-flex items-center gap-1 rounded border border-emerald-700 bg-emerald-900/40 px-2 py-1 text-xs text-emerald-200 hover:bg-emerald-900/60 disabled:opacity-50"
        >
          <Save className="h-3 w-3" />
          save
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
        >
          <X className="h-3 w-3" />
          cancel
        </button>
      </div>
    </form>
  );
}
